import {SceneMode, Viewer} from 'cesium';
import * as Cesium from 'cesium';
import {Subscription} from 'rxjs';
import {ElementRef} from '@angular/core';
import {BBox, MapperViewer} from './types';
import {FeatureCollection} from '@turf/turf';

export const canvasSize = ({width, height}, viewer: Viewer): void => {
  viewer.canvas.width = width;
  viewer.canvas.height = height;
  viewer.canvas.style.width = width + 'px';
  viewer.canvas.style.height = height + 'px';
  viewer.canvas.style.transform = `scale(${document.body.clientHeight * 0.80 / height})`;
};

export const setMapRenderMode = (mode: '2d' | '3d', viewer: Viewer): void =>  {
  if (mode === '2d') {
    viewer.scene.morphTo2D(0);
    setMapCanvasSize({
      ratio: 1.357,
      size: document.body.clientHeight * 0.80,
      viewer
    });
  } else {
    viewer.scene.morphTo3D(0);
    setMapCanvasSize({
      ratio: 1,
      size: document.body.clientHeight * 0.80,
      viewer
    });
  }

  viewer.render();
};

export const setMapCanvasSize = ({ratio, size, viewer}: {
  ratio: number,
  size: number,
  viewer: Viewer
}): void => {
  canvasSize({width: size * ratio, height: size}, viewer);
};


export const normalize2dMultipolygon = (array): any[] =>  {
  return array.map(a => {
    return a.map(b => b.map(c => ([c[0], c[1]])));
  });
};


export const optimizePolygon = (points, outputMaxPointCount = 100): any[] => {
  if (points.length < 100) {
    return Cesium.Cartesian3.fromDegreesArray(points.flat());
  }

  const polygonOpti: any = [];
  let a = 0;
  for (let i = 0; i < points.length; i ++) {
    a++;
    if (a === Math.round(points.length / outputMaxPointCount)) {
      a = 0;
      polygonOpti.push(points[i]);
    }
  }

  return Cesium.Cartesian3.fromDegreesArray(polygonOpti.flat());
};

export const multiPolygonToCartesianPolygonHierarchies = (arr): any[] => {
  const deepCopy = JSON.parse(JSON.stringify(arr));
  const result = [];

  deepCopy.forEach(a => {
    a.forEach(b => {
      result.push({positions: b.map(c => Cesium.Cartesian3.fromDegreesArray(c)).flat()});
    });
  });

  return result;
};

export const distanceVisibleInCamera = (viewer: Viewer): number => {
  const {west, north, east, south} = getCameraBoundingBox(viewer);

  const startCartesian3Point = Cesium.Cartesian3.fromDegrees(north, west);
  const endCartesian3Point = Cesium.Cartesian3.fromDegrees(south, east);

  const startCartographicPoint = Cesium.Cartographic.fromCartesian(startCartesian3Point);
  const endCartographicPoint = Cesium.Cartographic.fromCartesian(endCartesian3Point);

  const ellipsoidGeodesic = new Cesium.EllipsoidGeodesic(startCartographicPoint,
    endCartographicPoint );
  const distance = ellipsoidGeodesic.surfaceDistance;

  return distance * 0.001;
};

export const getCamera = (viewer) => {
  const camera = viewer.scene.camera;
  const obj = {
    destination: camera.position,
    orientation: {
      heading: camera.heading,
      pitch: camera.pitch,
      roll: camera.roll,
    }
  };

  return JSON.stringify(obj);
};

export const getCameraBoundingBox = (viewer: Viewer): any => {
  const scene = viewer.scene;
  const camera = scene.camera;
  const canvas = scene.canvas;
  const ellipsoid = scene.globe.ellipsoid;

  const corners = [
    new Cesium.Cartesian2(0, 0),
    new Cesium.Cartesian2(canvas.width, 0),
    new Cesium.Cartesian2(0, canvas.height),
    new Cesium.Cartesian2(canvas.width, canvas.height)
  ];

  let west = Infinity;
  let east = -Infinity;
  let south = Infinity;
  let north = -Infinity;

  corners.forEach(corner => {
    const ray = camera.getPickRay(corner);
    if (ray) {
      const intersection = scene.globe.pick(ray, scene);
      if (intersection) {
        const cartographic = ellipsoid.cartesianToCartographic(intersection);
        const longitude = Cesium.Math.toDegrees(cartographic.longitude);
        const latitude = Cesium.Math.toDegrees(cartographic.latitude);

        west = Math.min(west, longitude);
        east = Math.max(east, longitude);
        south = Math.min(south, latitude);
        north = Math.max(north, latitude);
      }
    }
  });

  if (west === Infinity || east === -Infinity || south === Infinity || north === -Infinity) {
    return {
      west: 0,
      north: 0,
      east: 0,
      south: 0,
    };
  }

  // Gestion du cas où la bbox traverse le méridien 180°
  if (west > east) {
    if (Math.abs(west) > Math.abs(east)) {
      west -= 360;
    } else {
      east += 360;
    }
  }

  return {
    west: +west.toFixed(4),
    north: +north.toFixed(4),
    east: +east.toFixed(4),
    south: +south.toFixed(4),
  };
};

export const toBBoxString = (rectangleCords): any => {
  return rectangleCords.west + ',' + rectangleCords.north + ',' + rectangleCords.east + ',' + rectangleCords.south;
};

export const toIGNBBoxString = (rectangleCords): any => {
  return `${rectangleCords.south},${rectangleCords.west},${rectangleCords.north},${rectangleCords.east}`
};

export const createNode = (n, v): any => {
  n = document.createElementNS("http://www.w3.org/2000/svg", n);
  for (var p in v)
    n.setAttribute(p, v[p]);
  return n
};

export const drawSvg = (num: string, priority: string): Promise<HTMLImageElement> => {
  return new Promise((resolve) => {
    const logicalSize = 128;
    const pixelRatio = (window as any).devicePixelRatio || 1;
    const physicalSize = logicalSize * pixelRatio;

    const svg = createNode('svg', {
      width: physicalSize,
      height: physicalSize,
      viewBox: '0 0 128 128',
      xmlns: "http://www.w3.org/2000/svg",
      "shape-rendering": "geometricPrecision",
      "text-rendering": "geometricPrecision"
    });

    const color = priority === 'Elevée' ? '#f1c40f' : '#000';
    const colorText = priority === 'Elevée' ? '#000' : '#FFF';

    const circle = createNode('circle', {
      cx: '64',
      cy: '64',
      r: '64',
      fill: color,
    });

    const number = createNode('text', {
      "font-weight": "bold",
      "text-anchor": "middle",
      "dominant-baseline": "middle",
      "font-family": "Roboto, Helvetica, Arial, sans-serif",
      "font-size": "64",
      "x": "64",
      "y": "68",
      "fill": colorText,
    });

    number.innerHTML = num;

    svg.append(circle);
    svg.append(number);

    const outerHTML = svg.outerHTML;
    const blob = new Blob([outerHTML], { type: 'image/svg+xml;charset=utf-8' });

    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => {
      const base64data = reader.result as string;
      const img = new Image();

      img.width = logicalSize;
      img.height = logicalSize;

      img.src = base64data;
      resolve(img);
    };
  });
};


export const buildOrDivide = (res, bbox, url, buildFn, maxReturned): Promise<void> => {
  if (res.numberMatched > maxReturned) {

    const cleanedBbox = typeof bbox === 'string' ? bbox.split(',').map(a => Number(a)) : bbox;
    const bboxArray = subdivideBbox(cleanedBbox);

    return Promise.all(
      bboxArray.map((_bbox) => {
        const stringBBox = _bbox.join(',');

        return fetch(url + stringBBox).then(raw => raw.json()).then(_res => {
          return buildOrDivide(_res, _bbox, url, buildFn, maxReturned);
        });
      })
    ).then(() => {});
  }

  buildFn(res);

  return Promise.resolve();
};

export const subdivideBbox = (bbox) => {
  const [minLon, minLat, maxLon, maxLat] = bbox;
  const midLon = minLon + (maxLon - minLon) / 2;
  const midLat = minLat + (maxLat - minLat) / 2;

  // Vérifier si la BBOX est trop petite pour être subdivisée (évite boucle infinie si précision flottante)
  const lonDiff = Math.abs(maxLon - minLon);
  const latDiff = Math.abs(maxLat - minLat);

  // Seuil de taille minimale pour la subdivision
  const minSizeThreshold = 1e-9;
  if (lonDiff < minSizeThreshold || latDiff < minSizeThreshold) {
    console.warn(`BBOX ${bbox.join(',')} trop petite pour subdivision, arrêt.`);
    return []; // Retourne un tableau vide pour ne pas continuer la récursion
  }

  // [minLon, minLat, midLon, midLat] // Sud-Ouest (Bottom-Left)
  // [midLon, minLat, maxLon, midLat] // Sud-Est (Bottom-Right)
  // [minLon, midLat, midLon, maxLat] // Nord-Ouest (Top-Left)
  // [midLon, midLat, maxLon, maxLat] // Nord-Est (Top-Right)

  return [
    [minLon, minLat, midLon, midLat],
    [midLon, minLat, maxLon, midLat],
    [minLon, midLat, midLon, maxLat],
    [midLon, midLat, maxLon, maxLat]
  ];
};

export const screenshot = (
  viewer: MapperViewer,
  canvasSnapshotRef: ElementRef,
  echelleFixedValue,
  event?: Subscription): void => {

  const baseScreenshot = viewer.canvas.toDataURL('image/jpeg', 1);

  // download(baseScreenshot);

  ////// SCREENSHOT
  // create a canvas

  const ctx: CanvasRenderingContext2D = canvasSnapshotRef.nativeElement.getContext('2d');

  ctx.clearRect(0, 0, canvasSnapshotRef.nativeElement.width, canvasSnapshotRef.nativeElement.height);



  const img = document.createElement('img');
  img.src = baseScreenshot;
  img.addEventListener('load', () => {
    /**
     * The actual map screenshot
     */
    ctx.drawImage(img, 0, 0);
    /**
     * The scale
     */
    const originLeft = 1500 - (echelleFixedValue.barWidthComputed + 150);

    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.shadowBlur = 0;

    // background
    ctx.beginPath();
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.rect(originLeft, 1500 - 50, echelleFixedValue.barWidthComputed + 150, 50);
    ctx.fill();
    ctx.closePath();

    // label
    ctx.fillStyle = 'black';
    ctx.textAlign = 'start';
    ctx.font = '30px Arial';
    ctx.fillText(echelleFixedValue.distanceLabel, originLeft + echelleFixedValue.barWidthComputed + 50, 1500 - 15);

    //// BAR@
    ctx.beginPath();
    ctx.fillStyle = 'black';
    //left
    ctx.rect(originLeft + 30, 1500 - 29, 3, 15);
    ctx.fill();
    //bottom
    ctx.rect(originLeft + 30, 1500 - 15, echelleFixedValue.barWidthComputed, 3);
    ctx.fill();
    //right
    ctx.rect(originLeft + 27 + echelleFixedValue.barWidthComputed, 1500 - 30, 3, 15);
    ctx.fill();
    ctx.closePath();


    /**
     * The arrow
     */
    const arrow = document.createElement('img');
    arrow.src = '../../assets/images/north-arrow.png';
    arrow.addEventListener('load', () => {
      const imgSize = 100;

      ctx.save();
      ctx.translate(1400 + imgSize / 2,  imgSize / 2);
      ctx.rotate(-viewer.camera.heading);
      ctx.drawImage(arrow, imgSize / 2 * (-1), imgSize / 2 * (-1), imgSize, imgSize);
      ctx.restore();


      const finalScreenshot = viewer.canvas.toDataURL('image/jpeg', 1);

      download(finalScreenshot);


      // const blob = this.downloadService.dataURItoBlob(finalScreenshot);
    });
  });
};

export const download = (href: string): void => {
  const link = document.createElement('a');
  link.download = 'screenshot.jpg';
  link.href = href;
  link.click();
};


export const isBBoxInRadians = (bbox: BBox): boolean => {
  const all = [bbox.west, bbox.south, bbox.east, bbox.north];
  return all.every(v => Math.abs(v) <= Math.PI);
};

export const toDegreesBBox = (bbox: BBox): BBox => {
  return {
    west: Cesium.Math.toDegrees(bbox.west),
    south: Cesium.Math.toDegrees(bbox.south),
    east: Cesium.Math.toDegrees(bbox.east),
    north: Cesium.Math.toDegrees(bbox.north),
  };
};

export const normalizeBBoxToDegrees = (bbox: BBox): BBox => {
  return isBBoxInRadians(bbox) ? toDegreesBBox(bbox) : bbox;
};


// convert multypolygon to polygon from a featureCollection (
export const convertMultiPolygonToPolygon = (featureCollection: any): any => {
  const features = featureCollection.features.map((feature) => {
    if (feature.geometry.type === 'MultiPolygon') {
      const coordinates = feature.geometry.coordinates.map((polygon) => {
        return polygon.map((ring) => {
          return ring.map((coord) => {
            return coord;
          });
        });
      });

      return {
        ...feature,
        geometry: {
          ...feature.geometry,
          type: 'Polygon',
          coordinates: coordinates[0][0],
        },
      };
    }
    return feature;
  });

  return {
    ...featureCollection,
    features,
  };
};

export const getFontSizeRenderByCanvasRatio = (viewer) => {
  const printedDocSize = viewer.canvasMode === '2d' ? 6785 : 1500;
  const printedDocFontSize = 25;
  const diffBetweenScreenAndScreenshotSize = (printedDocSize / viewer.canvas.width);

  return {
    font: (printedDocFontSize / diffBetweenScreenAndScreenshotSize) + 'px sans-serif',
    backgroundPadding: new Cesium.Cartesian2(3, 2),
  };
};

export const updateDataSourceLabelSize = (dataSource, viewer): void => {
  const {font} = getFontSizeRenderByCanvasRatio(viewer);
  dataSource.entities.values.forEach((entity) => {
    if (entity.label) {
      entity.label.font = font;
    }
  });
};


export const normalizeMultiPolygonCoordinates = (coordinates: any[]): any[] =>  {
  return coordinates.map(polygon => {
    return polygon.map(ring => {
      return ring.map(coord => {
        return [coord[0], coord[1]];
      });
    });
  });
}

export const normalizePolygonCoordinates = (coordinates: any[]): any[] =>  {
  return coordinates.map(coord => {
    return [coord[0], coord[1]];
  });
}

/**
 * Bugfix : ClampToGround
 * Néttoie les coordonnées des polygones et multipolygones d'une FeatureCollection qui comporte des données d'altitudes
 * ([lon, lat, z]) en ne gardant que [lon, lat]
 * @param featureCollection
 */
export const normalizePolygonCollection = (featureCollection: FeatureCollection): any => {
  const features = featureCollection.features.map((feature) => {
    let coordinates = feature.geometry.coordinates;
    if (feature.geometry.type === 'Polygon') {
      coordinates = normalizePolygonCoordinates(feature.geometry.coordinates);
    }

    if (feature.geometry.type === 'MultiPolygon') {
      coordinates = normalizeMultiPolygonCoordinates(feature.geometry.coordinates);
    }

    return {
      ...feature,
      geometry: {
        ...feature.geometry,
        coordinates,
      },
    };
  });

  return {
    ...featureCollection,
    features,
  };
};
